/*
 * BajadaRentaDossier.jsx — Dosier comercial en PDF para el propietario.
 *
 * Componente auto-contenido que se abre desde BajadaRenta.jsx sobre una
 * negociación existente. Renderiza un documento tipo "propuesta corporativa"
 * (A4) con:
 *   - Portada con marca ADQUIRIAM estilizada
 *   - Resumen ejecutivo (renta actual, propuesta, ahorro, servicios)
 *   - Carta comercial formal al propietario
 *   - Tabla de servicios preventivos ofrecidos + calendario
 *   - Balance económico global hasta fin de contrato
 *   - Análisis normativo Adquiriam (opcional, obligaciones seleccionables)
 *   - Cláusulas y firma
 *
 * Salida: PDF real generado con jsPDF + html2canvas.
 */
import React, { useRef, useState, useMemo } from 'react';
import { XCircle, Download, Loader2, FileText, ScrollText, CheckSquare, Square } from 'lucide-react';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const URGENCY_META = {
  alta:  { color: '#b91c1c', bg: '#fee2e2', label: 'ALTA' },
  media: { color: '#b45309', bg: '#fef3c7', label: 'MEDIA' },
  baja:  { color: '#475569', bg: '#e2e8f0', label: 'BAJA' },
};
const CATEGORY_LABEL = {
  ITE: 'ITE / IEE', accesibilidad: 'Accesibilidad', ascensor: 'Ascensor',
  energetica: 'Rehab. energética', amianto: 'Amianto', incendios: 'Incendios',
  electrica: 'Eléctrica / OCA', renovables: 'Renovables', urbanistica: 'Urbanismo',
  arrendamiento: 'Arrendamiento', otro: 'Otro',
};

const eur = (v) => (Number(v) || 0).toLocaleString('es-ES', { maximumFractionDigits: 0 });
const eur2 = (v) => (Number(v) || 0).toLocaleString('es-ES', { maximumFractionDigits: 2, minimumFractionDigits: 2 });
const todayLong = () => new Date().toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' });

const freqLabel = (m) => {
  const months = Number(m) || 12;
  if (months === 1) return 'mensual';
  if (months === 3) return 'trimestral';
  if (months === 6) return 'semestral';
  if (months === 12) return 'anual';
  if (months === 24) return 'bienal';
  if (months === 36) return 'trienal';
  if (months % 12 === 0) return `cada ${months / 12} años`;
  return `cada ${months} meses`;
};

const computeExecs = (yearsToEnd, freqMonths) => {
  const freq = (Number(freqMonths) || 12) / 12;
  if (freq <= 0) return 0;
  return Math.floor(yearsToEnd / freq) + (yearsToEnd % freq >= freq / 2 ? 1 : 0);
};

const BajadaRentaDossier = ({ neg, onClose }) => {
  const printRef = useRef(null);
  const [exporting, setExporting] = useState(false);

  // ------- Análisis normativo: obligaciones seleccionables para incluir -------
  const allObligations = useMemo(() => {
    return (neg.regulatory_analysis?.obligations || []).map((ob, i) => ({
      ...ob,
      _id: ob.id || `ob-${i}`,
    }));
  }, [neg.regulatory_analysis]);
  const [includedObs, setIncludedObs] = useState(() => new Set()); // por defecto ninguna
  const toggleOb = (id) => setIncludedObs((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const toggleAllObs = () => setIncludedObs((prev) => {
    if (prev.size === allObligations.length) return new Set();
    return new Set(allObligations.map((o) => o._id));
  });
  const selectedObs = allObligations.filter((o) => includedObs.has(o._id));

  // ------- Cálculos financieros -------
  const financials = useMemo(() => {
    const curMonthly = Number(neg.current_rent_monthly) || 0;
    const targetMonthly = Number(neg.final_rent_monthly ?? neg.target_rent_monthly) || 0;
    const currentAnnual = curMonthly * 12;
    const proposedAnnual = targetMonthly * 12;
    const annualSaving = Math.max(0, currentAnnual - proposedAnnual);
    const reductionPct = currentAnnual > 0 ? (annualSaving / currentAnnual) * 100 : 0;

    let yearsToEnd = 1;
    let contractEnd = null;
    if (neg.contract_end_date) {
      const end = new Date(neg.contract_end_date);
      if (!isNaN(end.getTime())) {
        contractEnd = end;
        yearsToEnd = Math.max(0, (end.getTime() - Date.now()) / (365.25 * 24 * 60 * 60 * 1000));
      }
    } else if (neg.service_offer?.duration_years) {
      yearsToEnd = Number(neg.service_offer.duration_years);
    }

    const items = neg.service_offer?.items || [];
    const servicesRows = items.map((it) => {
      const unit = Number(it.market_value_eur) || 0;
      const freqMonths = Number(it.frequency_months) || 12;
      const freqYears = freqMonths / 12;
      const execs = computeExecs(yearsToEnd, freqMonths);
      const total = unit * execs;
      // Coste anual equivalente: reparte el coste de una ejecución entre los años
      // que cubre esa frecuencia. Para servicios anuales o infra-anuales coincide
      // con el coste anual habitual.
      const annualEq = freqYears > 0 ? unit / freqYears : unit;
      return {
        title: it.title,
        frequency: freqLabel(freqMonths),
        isMultiYear: freqMonths > 12,
        unit,
        execs,
        total,
        annualEq,
      };
    });
    const servicesTotal = servicesRows.reduce((s, r) => s + r.total, 0);
    const servicesAnnualTotal = servicesRows.reduce((s, r) => s + r.annualEq, 0);

    const totalRentSavedForOwner = annualSaving * yearsToEnd;
    const totalOwnerBenefit = totalRentSavedForOwner + servicesTotal;

    return {
      curMonthly, targetMonthly, currentAnnual, proposedAnnual, annualSaving, reductionPct,
      yearsToEnd, contractEnd, servicesRows, servicesTotal, servicesAnnualTotal,
      totalRentSavedForOwner, totalOwnerBenefit,
    };
  }, [neg]);

  // ------- Exportar PDF -------
  const exportPDF = async () => {
    if (!printRef.current) return;
    setExporting(true);
    try {
      const pages = printRef.current.querySelectorAll('[data-dossier-page]');
      const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
      const pageWidth = pdf.internal.pageSize.getWidth();   // 210
      const pageHeight = pdf.internal.pageSize.getHeight(); // 297

      for (let i = 0; i < pages.length; i++) {
        const canvas = await html2canvas(pages[i], {
          scale: 2, backgroundColor: '#ffffff', useCORS: true, logging: false,
        });
        const imgData = canvas.toDataURL('image/jpeg', 0.95);
        // Ajusta imagen al ancho de página, mantiene proporción.
        const imgWidth = pageWidth;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        if (i > 0) pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, 0, imgWidth, Math.min(imgHeight, pageHeight));
      }
      const siteRef = (neg.site_id || 'site').toString().replace(/[^a-zA-Z0-9_-]/g, '_');
      pdf.save(`Dosier_BajadaRenta_${siteRef}.pdf`);
      toast.success('Dosier PDF generado');
    } catch (e) {
      console.error(e);
      toast.error('Error generando el PDF');
    } finally {
      setExporting(false);
    }
  };

  const f = financials;
  const contractEndLabel = f.contractEnd
    ? f.contractEnd.toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' })
    : 'a definir';
  const ownerName = neg.owner_name || 'la Propiedad';
  const siteAddress = neg.site_address || neg.site_name || '';

  return (
    <div className="fixed inset-0 z-[10070] bg-black/90 backdrop-blur flex items-center justify-center p-2 sm:p-4"
      onClick={onClose} data-testid="dossier-modal">
      <div onClick={(e) => e.stopPropagation()}
        className="w-full max-w-5xl max-h-[96vh] flex flex-col bg-[#0a0a0a] border border-white/15 rounded-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-white/10 bg-gradient-to-r from-[#A3FF12]/10 to-transparent">
          <div className="flex items-center gap-2 min-w-0">
            <FileText size={16} className="text-[#A3FF12]"/>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-[#A3FF12] font-black">Dosier comercial</div>
              <div className="text-white text-sm font-black truncate">
                Bajada de renta · {neg.site_id} {neg.site_name ? `— ${neg.site_name}` : ''}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button onClick={exportPDF} disabled={exporting}
              data-testid="btn-export-dossier-pdf"
              className="bg-[#A3FF12] hover:bg-[#b8ff3a] text-black text-xs font-black px-3 py-2 rounded-lg flex items-center gap-1 disabled:opacity-40">
              {exporting ? <Loader2 className="animate-spin" size={12}/> : <Download size={12}/>}
              {exporting ? 'Generando…' : 'Descargar PDF'}
            </button>
            <button onClick={onClose} data-testid="btn-close-dossier"
              className="text-white/50 hover:text-white p-1"><XCircle size={22}/></button>
          </div>
        </div>

        {/* Preview scrollable — pages */}
        <div className="flex-1 flex overflow-hidden">
          {/* Sidebar: selección de obligaciones normativas a incluir en el dosier */}
          {allObligations.length > 0 && (
            <div className="w-72 flex-shrink-0 border-r border-white/10 bg-black/40 overflow-y-auto p-3"
              data-testid="dossier-obligations-sidebar">
              <div className="flex items-center gap-2 mb-2">
                <ScrollText size={13} className="text-blue-300"/>
                <span className="text-[10px] uppercase tracking-widest text-blue-300 font-black">
                  Análisis normativo
                </span>
              </div>
              <div className="text-[11px] text-white/60 mb-3 leading-snug">
                Selecciona qué obligaciones detectadas incluir como página extra
                del dosier ({selectedObs.length}/{allObligations.length}).
              </div>
              <button onClick={toggleAllObs}
                data-testid="btn-toggle-all-obligations"
                className="w-full mb-2 text-[11px] font-black text-white/80 hover:text-white bg-white/5 hover:bg-white/10 rounded-lg py-1.5 flex items-center justify-center gap-1">
                {includedObs.size === allObligations.length
                  ? <><Square size={12}/> Deseleccionar todo</>
                  : <><CheckSquare size={12}/> Seleccionar todo</>}
              </button>
              <ul className="space-y-1.5">
                {allObligations.map((ob) => {
                  const urg = URGENCY_META[ob.urgency] || URGENCY_META.media;
                  const checked = includedObs.has(ob._id);
                  return (
                    <li key={ob._id}>
                      <label className={`flex items-start gap-2 p-2 rounded-lg cursor-pointer border transition-colors ${checked ? 'bg-[#A3FF12]/10 border-[#A3FF12]/40' : 'bg-black/30 border-white/10 hover:border-white/30'}`}
                        data-testid={`chk-dossier-ob-${ob._id}`}>
                        <input type="checkbox" checked={checked} onChange={() => toggleOb(ob._id)}
                          className="mt-0.5 w-3.5 h-3.5 accent-[#A3FF12] flex-shrink-0"/>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1 mb-0.5">
                            <span className="text-[8px] font-black px-1 py-0.5 rounded flex-shrink-0"
                              style={{ backgroundColor: `${urg.color}20`, color: urg.color, border: `1px solid ${urg.color}50` }}>
                              {urg.label}
                            </span>
                            <span className="text-[8px] font-bold text-blue-300 bg-blue-500/10 px-1 py-0.5 rounded truncate">
                              {CATEGORY_LABEL[ob.category] || ob.category?.toUpperCase() || 'OTRO'}
                            </span>
                          </div>
                          <div className="text-white text-[11px] font-bold leading-tight">
                            {ob.norma || 'Sin título'}
                          </div>
                          {ob.deadline && (
                            <div className="text-white/50 text-[10px] mt-0.5">Plazo: {ob.deadline}</div>
                          )}
                        </div>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          {/* Preview: páginas del PDF */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-neutral-800/40">
            <div ref={printRef} className="mx-auto flex flex-col items-center gap-5">
              {/* --------- PAGE 1: PORTADA + RESUMEN EJECUTIVO --------- */}
              <DossierPage>
                <CoverBlock ownerName={ownerName} siteId={neg.site_id} siteAddress={siteAddress}/>
                <ExecutiveSummary f={f} contractEndLabel={contractEndLabel}/>
                <PageFooter siteId={neg.site_id} page={1}/>
              </DossierPage>

              {/* --------- PAGE 2: CARTA COMERCIAL + BENEFICIO --------- */}
              <DossierPage>
                <SectionTitle idx="01" title="Carta al propietario"/>
                <CommercialLetter neg={neg} f={f} ownerName={ownerName} contractEndLabel={contractEndLabel}/>
                <PageFooter siteId={neg.site_id} page={2}/>
              </DossierPage>

              {/* --------- PAGE 3: SERVICIOS + BALANCE --------- */}
              <DossierPage>
                <SectionTitle idx="02" title="Servicios preventivos ofrecidos como contrapartida"/>
                <ServicesTable rows={f.servicesRows} servicesTotal={f.servicesTotal} servicesAnnualTotal={f.servicesAnnualTotal} yearsToEnd={f.yearsToEnd}/>
                <SectionTitle idx="03" title="Balance económico para la Propiedad"/>
              <FinancialBalance f={f}/>
              <ClosingClauses/>
              <Signature/>
              <PageFooter siteId={neg.site_id} page={3}/>
            </DossierPage>

            {/* --------- PAGE 4: ANÁLISIS NORMATIVO (opcional) --------- */}
            {selectedObs.length > 0 && (
              <DossierPage>
                <SectionTitle idx="04" title="Anexo — Obligaciones normativas del inmueble"/>
                <RegulatoryAnnex obligations={selectedObs}/>
                <PageFooter siteId={neg.site_id} page={4}/>
              </DossierPage>
            )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Sub-componentes de layout — pensados para renderizarse a 794px de ancho
// (= 210mm a 96dpi). Cada página se pinta como un canvas y se apila en jsPDF.
// ============================================================================

const DossierPage = ({ children }) => (
  <div data-dossier-page
    className="bg-white text-neutral-900 shadow-2xl relative"
    style={{
      width: '794px',
      minHeight: '1123px',
      padding: '56px 64px',
      fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
      lineHeight: 1.55,
    }}>
    {children}
  </div>
);

const CoverBlock = ({ ownerName, siteId, siteAddress }) => (
  <div className="mb-10">
    <div className="flex items-start justify-between gap-4">
      <div>
        <div style={{
          fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
          fontWeight: 900,
          fontSize: '32px',
          letterSpacing: '4px',
          color: '#0f172a',
        }}>
          ADQUIR<span style={{ color: '#65a30d' }}>I</span>AM
        </div>
        <div style={{ fontSize: '10px', letterSpacing: '3px', color: '#64748b', marginTop: '2px' }}>
          GESTIÓN INTEGRAL DE EMPLAZAMIENTOS · TELECOM
        </div>
      </div>
      <div className="text-right">
        <div style={{ fontSize: '10px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '2px' }}>
          Documento
        </div>
        <div style={{ fontSize: '11px', color: '#0f172a', fontWeight: 700 }}>
          {todayLong()}
        </div>
      </div>
    </div>
    <div style={{ height: '3px', background: '#65a30d', width: '80px', margin: '28px 0' }}/>
    <div style={{ fontSize: '11px', letterSpacing: '3px', color: '#64748b', textTransform: 'uppercase' }}>
      Propuesta comercial confidencial
    </div>
    <div style={{ fontSize: '38px', fontWeight: 900, color: '#0f172a', lineHeight: 1.1, marginTop: '10px' }}>
      Renegociación de<br/>arrendamiento
    </div>
    <div style={{ fontSize: '15px', color: '#475569', marginTop: '18px', maxWidth: '560px' }}>
      Propuesta económica y de servicios preventivos in-kind dirigida a <b style={{ color: '#0f172a' }}>{ownerName}</b>,
      titular del contrato de arrendamiento del emplazamiento{' '}
      <b style={{ color: '#0f172a' }}>{siteId}</b>{siteAddress ? <> — {siteAddress}</> : null}.
    </div>
  </div>
);

const ExecutiveSummary = ({ f, contractEndLabel }) => (
  <div style={{
    marginTop: '20px', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '20px 22px',
    background: '#f8fafc',
  }}>
    <div style={{ fontSize: '10px', letterSpacing: '3px', color: '#64748b', textTransform: 'uppercase', marginBottom: '10px' }}>
      Resumen ejecutivo
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '14px' }}>
      <KpiCell label="Renta actual" value={`${eur(f.currentAnnual)} €/año`}/>
      <KpiCell label="Renta propuesta" value={`${eur(f.proposedAnnual)} €/año`}/>
      <KpiCell label="Ahorro anual" value={`${eur(f.annualSaving)} €`} accent/>
      <KpiCell label="Reducción" value={`${f.reductionPct.toFixed(1)}%`} accent/>
    </div>
    <div style={{
      marginTop: '14px', padding: '14px 16px', background: '#0f172a', color: '#f8fafc',
      borderRadius: '10px', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '14px',
    }}>
      <SummaryLine k="Vigencia hasta" v={contractEndLabel}/>
      <SummaryLine k="Servicios ofrecidos" v={`${f.servicesRows.length} preventivos`}/>
      <SummaryLine k="Beneficio total propiedad" v={`${eur(f.totalOwnerBenefit)} €`} accent/>
    </div>
    <div style={{ fontSize: '11.5px', color: '#475569', marginTop: '14px' }}>
      El presente dosier detalla la propuesta económica de <b>renegociación de la renta</b> y
      el <b>paquete de servicios preventivos</b> que Adquiriam se compromete a ejecutar como
      contrapartida a la ajuste, con el objetivo de mantener el emplazamiento en óptimas
      condiciones y prolongar la relación contractual con la Propiedad.
    </div>
  </div>
);

const KpiCell = ({ label, value, accent }) => (
  <div>
    <div style={{ fontSize: '9.5px', letterSpacing: '2px', color: '#64748b', textTransform: 'uppercase' }}>{label}</div>
    <div style={{
      fontSize: '20px', fontWeight: 900, marginTop: '4px',
      color: accent ? '#65a30d' : '#0f172a',
    }}>{value}</div>
  </div>
);

const SummaryLine = ({ k, v, accent }) => (
  <div>
    <div style={{ fontSize: '9.5px', letterSpacing: '2px', color: '#94a3b8', textTransform: 'uppercase' }}>{k}</div>
    <div style={{ fontSize: '13px', fontWeight: 700, color: accent ? '#A3FF12' : '#f8fafc', marginTop: '3px' }}>{v}</div>
  </div>
);

const SectionTitle = ({ idx, title }) => (
  <div style={{ display: 'flex', alignItems: 'baseline', gap: '14px', margin: '4px 0 18px' }}>
    <div style={{ fontSize: '11px', letterSpacing: '3px', color: '#65a30d', fontWeight: 900 }}>{idx}</div>
    <div style={{ fontSize: '20px', fontWeight: 900, color: '#0f172a' }}>{title}</div>
    <div style={{ flex: 1, height: '1px', background: '#e2e8f0', marginLeft: '10px' }}/>
  </div>
);

const CommercialLetter = ({ neg, f, ownerName, contractEndLabel }) => (
  <div style={{ fontSize: '13px', color: '#1f2937', lineHeight: 1.75 }}>
    <div style={{ marginBottom: '14px' }}>
      <b>A la atención de {ownerName}</b><br/>
      <span style={{ color: '#64748b' }}>Ref.: contrato de arrendamiento sobre el emplazamiento {neg.site_id}</span>
    </div>
    <p>Estimados Sres./Sras.,</p>
    <p>
      Desde <b>Adquiriam</b>, gestora de la cartera de emplazamientos de telecomunicaciones,
      nos ponemos en contacto con ustedes con el objeto de trasladar una <b>propuesta formal
      de renegociación</b> del contrato de arrendamiento actualmente vigente sobre el
      emplazamiento con referencia <b>{neg.site_id}</b>.
    </p>
    <p>
      La renta actual asciende a <b>{eur(f.currentAnnual)} €/año</b>. La coyuntura del sector
      —consolidación de operadores, redimensionamiento de emplazamientos y presión sobre los
      costes de red— exige un ajuste de las condiciones económicas si queremos garantizar la
      continuidad del emplazamiento hasta su vencimiento previsto en <b>{contractEndLabel}</b>.
      En ese marco, proponemos una nueva renta de <b>{eur(f.proposedAnnual)} €/año</b>, lo que
      supone una reducción del <b>{f.reductionPct.toFixed(1)}%</b>.
    </p>
    <p>
      Consciente de que esta reducción debe ir acompañada de valor real para la Propiedad,
      Adquiriam <b>se compromete a ejecutar por su cuenta un paquete de servicios
      preventivos</b> sobre el inmueble (limpieza técnica, revisión de impermeabilización,
      desbroce, revisiones eléctricas y aquellos incluidos en el detalle adjunto). Estos
      trabajos, con un <b>valor de mercado agregado de {eur(f.servicesTotal)} €</b> hasta el
      vencimiento del contrato, prolongan la vida útil del bien y evitan a la Propiedad
      cualquier desembolso por mantenimiento del entorno del emplazamiento.
    </p>
    <p>
      El beneficio neto acumulado para ustedes —descontando el ajuste de renta— asciende a
      <b> {eur(f.totalOwnerBenefit)} €</b> hasta {contractEndLabel}, tal como se detalla en la
      página siguiente.
    </p>
    <p>
      Quedamos a su disposición para concertar una reunión y resolver cualquier duda. Estamos
      convencidos de que esta propuesta es <b>equilibrada, sostenible y favorable para ambas
      partes</b>, y confiamos en poder formalizarla en las próximas semanas.
    </p>
    <p style={{ marginTop: '20px' }}>Reciban un cordial saludo,</p>
  </div>
);

const ServicesTable = ({ rows, servicesTotal, servicesAnnualTotal, yearsToEnd }) => (
  <div style={{ marginBottom: '22px' }}>
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
      <thead>
        <tr style={{ background: '#0f172a', color: '#f8fafc' }}>
          <th style={thStyle}>Servicio</th>
          <th style={thStyle}>Frecuencia</th>
          <th style={{ ...thStyle, textAlign: 'right' }}>Valor unitario</th>
          <th style={{ ...thStyle, textAlign: 'center' }}>Ejecuciones</th>
          <th style={{ ...thStyle, textAlign: 'right' }}>Total periodo</th>
          <th style={{ ...thStyle, textAlign: 'right' }}>Anual equiv.</th>
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 && (
          <tr><td colSpan={6} style={{ padding: '18px', textAlign: 'center', color: '#94a3b8', fontStyle: 'italic' }}>
            No se ha definido oferta de servicios. Diseñe la oferta en la negociación antes de emitir el dosier.
          </td></tr>
        )}
        {rows.map((r, i) => (
          <tr key={i} style={{ background: i % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
            <td style={tdStyle}>
              {r.title}
              {r.isMultiYear && (
                <span style={{
                  marginLeft: '6px', fontSize: '9px', letterSpacing: '1px', textTransform: 'uppercase',
                  background: '#fef3c7', color: '#92400e', padding: '1px 5px', borderRadius: '4px', fontWeight: 700,
                }}>plurianual</span>
              )}
            </td>
            <td style={{ ...tdStyle, color: '#475569' }}>{r.frequency}</td>
            <td style={{ ...tdStyle, textAlign: 'right' }}>{eur2(r.unit)} €</td>
            <td style={{ ...tdStyle, textAlign: 'center' }}>{r.execs}</td>
            <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700 }}>{eur(r.total)} €</td>
            <td style={{ ...tdStyle, textAlign: 'right', color: '#475569' }}>{eur(r.annualEq)} €/año</td>
          </tr>
        ))}
        {rows.length > 0 && (
          <tr style={{ background: '#65a30d', color: '#f8fafc' }}>
            <td style={{ ...tdStyle, fontWeight: 900 }} colSpan={4}>
              Total valor entregado a la Propiedad ({yearsToEnd.toFixed(1)} años)
            </td>
            <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 900 }}>{eur(servicesTotal)} €</td>
            <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 900 }}>{eur(servicesAnnualTotal)} €/año</td>
          </tr>
        )}
      </tbody>
    </table>
    <div style={{ fontSize: '10.5px', color: '#64748b', marginTop: '8px', lineHeight: 1.5 }}>
      Adquiriam asumirá el 100% del coste de los servicios listados, incluyendo materiales,
      mano de obra, medios auxiliares y gestión de residuos, sin repercusión alguna sobre la
      Propiedad. La columna <b>Anual equiv.</b> reparte proporcionalmente el coste de cada
      servicio entre los años que cubre su frecuencia, facilitando la comparación con la renta
      anual.
    </div>
  </div>
);

const FinancialBalance = ({ f }) => (
  <div style={{ marginBottom: '20px' }}>
    <div style={{
      display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px',
    }}>
      <BalanceRow label="Ajuste de renta acumulado" value={`− ${eur(f.totalRentSavedForOwner)} €`} negative/>
      <BalanceRow label="Servicios ejecutados por Adquiriam" value={`+ ${eur(f.servicesTotal)} €`} positive/>
    </div>
    <div style={{
      marginTop: '14px', padding: '18px 22px',
      background: 'linear-gradient(90deg, #0f172a 0%, #1e293b 100%)',
      color: '#f8fafc', borderRadius: '12px',
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    }}>
      <div>
        <div style={{ fontSize: '10.5px', letterSpacing: '2px', color: '#94a3b8', textTransform: 'uppercase' }}>
          Beneficio total para la Propiedad hasta fin de contrato
        </div>
        <div style={{ fontSize: '11px', color: '#cbd5e1', marginTop: '4px' }}>
          (Renta actualizada + servicios preventivos ejecutados sin coste)
        </div>
      </div>
      <div style={{ fontSize: '28px', fontWeight: 900, color: '#A3FF12' }}>
        {eur(f.totalOwnerBenefit)} €
      </div>
    </div>
  </div>
);

const BalanceRow = ({ label, value, positive, negative }) => (
  <div style={{
    padding: '14px 16px', background: '#f8fafc', border: '1px solid #e2e8f0',
    borderRadius: '10px',
  }}>
    <div style={{ fontSize: '10.5px', letterSpacing: '2px', color: '#64748b', textTransform: 'uppercase' }}>
      {label}
    </div>
    <div style={{
      fontSize: '20px', fontWeight: 900, marginTop: '4px',
      color: positive ? '#65a30d' : (negative ? '#b91c1c' : '#0f172a'),
    }}>{value}</div>
  </div>
);

const ClosingClauses = () => (
  <div style={{ marginTop: '18px', fontSize: '11px', color: '#475569', lineHeight: 1.7 }}>
    <div style={{ fontWeight: 900, color: '#0f172a', fontSize: '12px', letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '6px' }}>
      Cláusulas principales
    </div>
    <ol style={{ paddingLeft: '18px', margin: 0 }}>
      <li>La entrada en vigor de la nueva renta se producirá con la firma de la adenda al contrato vigente.</li>
      <li>La ejecución de los servicios se planificará conforme al calendario técnico de Adquiriam, comunicando a la Propiedad con antelación cada intervención.</li>
      <li>El compromiso de servicios queda vinculado al mantenimiento de las nuevas condiciones económicas durante toda la vigencia del contrato.</li>
      <li>La presente propuesta tiene carácter confidencial y una validez de 30 días naturales desde su emisión.</li>
    </ol>
  </div>
);

const Signature = () => (
  <div style={{ marginTop: '28px', display: 'flex', justifyContent: 'space-between', gap: '30px' }}>
    <div style={{ flex: 1 }}>
      <div style={{ borderTop: '1px solid #0f172a', paddingTop: '6px', fontSize: '11px', color: '#0f172a', fontWeight: 700 }}>
        El equipo de Adquiriam
      </div>
      <div style={{ fontSize: '10px', color: '#64748b' }}>Fecha: {todayLong()}</div>
    </div>
    <div style={{ flex: 1 }}>
      <div style={{ borderTop: '1px solid #0f172a', paddingTop: '6px', fontSize: '11px', color: '#0f172a', fontWeight: 700 }}>
        La Propiedad
      </div>
      <div style={{ fontSize: '10px', color: '#64748b' }}>Firma y fecha</div>
    </div>
  </div>
);

const PageFooter = ({ siteId, page }) => (
  <div style={{
    position: 'absolute', bottom: '28px', left: '64px', right: '64px',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    fontSize: '9.5px', color: '#94a3b8', letterSpacing: '2px',
    borderTop: '1px solid #e2e8f0', paddingTop: '8px',
  }}>
    <span>ADQUIRIAM · DOSIER CONFIDENCIAL · {siteId}</span>
    <span>PÁG. {page.toString().padStart(2, '0')}</span>
  </div>
);

const RegulatoryAnnex = ({ obligations }) => (
  <div style={{ fontSize: '12px', color: '#1f2937' }}>
    <p style={{ marginTop: 0, marginBottom: '16px', color: '#475569', lineHeight: 1.6 }}>
      El equipo técnico de <b style={{ color: '#0f172a' }}>Adquiriam</b> ha analizado la
      normativa aplicable al inmueble y ha identificado las siguientes obligaciones a cargo
      de la Propiedad. Nuestra propuesta contempla apoyar económica y técnicamente a la
      Propiedad en el cumplimiento de las mismas, siempre que se alcance un acuerdo de
      renegociación de la renta.
    </p>
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {obligations.map((ob, i) => {
        const urg = URGENCY_META[ob.urgency] || URGENCY_META.media;
        return (
          <div key={ob._id || i} style={{
            border: '1px solid #e2e8f0', borderRadius: '10px', overflow: 'hidden',
            background: '#ffffff',
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              background: '#f8fafc', padding: '8px 12px',
              borderBottom: '1px solid #e2e8f0',
            }}>
              <span style={{
                fontSize: '9px', fontWeight: 900, letterSpacing: '1.5px', padding: '2px 6px',
                borderRadius: '4px', background: urg.bg, color: urg.color,
              }}>
                {urg.label}
              </span>
              <span style={{
                fontSize: '9px', fontWeight: 700, letterSpacing: '1px', padding: '2px 6px',
                borderRadius: '4px', background: '#dbeafe', color: '#1e40af',
              }}>
                {CATEGORY_LABEL[ob.category] || ob.category?.toUpperCase() || 'OTRO'}
              </span>
              <span style={{ flex: 1, fontSize: '12px', fontWeight: 900, color: '#0f172a' }}>
                {ob.norma || 'Obligación sin título'}
              </span>
              {ob.estimated_cost_eur && (
                <span style={{ fontSize: '10.5px', color: '#475569', fontWeight: 700 }}>
                  {ob.estimated_cost_eur}
                </span>
              )}
            </div>
            <div style={{ padding: '10px 14px' }}>
              {ob.obligation && (
                <div style={{ marginBottom: '8px' }}>
                  <div style={{ fontSize: '9px', letterSpacing: '2px', color: '#64748b', textTransform: 'uppercase', fontWeight: 700, marginBottom: '2px' }}>
                    Obligación
                  </div>
                  <div style={{ fontSize: '11.5px', color: '#1f2937', lineHeight: 1.55 }}>
                    {ob.obligation}
                  </div>
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: ob.our_leverage ? '8px' : 0 }}>
                {ob.deadline && (
                  <div>
                    <div style={{ fontSize: '9px', letterSpacing: '2px', color: '#64748b', textTransform: 'uppercase', fontWeight: 700, marginBottom: '2px' }}>
                      Plazo
                    </div>
                    <div style={{ fontSize: '11px', color: '#334155' }}>{ob.deadline}</div>
                  </div>
                )}
                {ob.estimated_cost_eur && (
                  <div>
                    <div style={{ fontSize: '9px', letterSpacing: '2px', color: '#64748b', textTransform: 'uppercase', fontWeight: 700, marginBottom: '2px' }}>
                      Coste estimado
                    </div>
                    <div style={{ fontSize: '11px', color: '#334155' }}>{ob.estimated_cost_eur}</div>
                  </div>
                )}
              </div>
              {ob.our_leverage && (
                <div style={{
                  background: '#f0fdf4', border: '1px solid #bbf7d0',
                  borderRadius: '6px', padding: '8px 10px',
                }}>
                  <div style={{ fontSize: '9px', letterSpacing: '2px', color: '#166534', textTransform: 'uppercase', fontWeight: 900, marginBottom: '2px' }}>
                    Propuesta de Adquiriam
                  </div>
                  <div style={{ fontSize: '11.5px', color: '#14532d', lineHeight: 1.5 }}>
                    {ob.our_leverage}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
    <div style={{ marginTop: '14px', fontSize: '10px', color: '#64748b', lineHeight: 1.5, fontStyle: 'italic' }}>
      Nota: el análisis normativo es orientativo y se basa en la información pública
      disponible sobre el inmueble a la fecha de emisión del dosier. La ejecución concreta
      de cada actuación se pactará por escrito con la Propiedad en documento anexo al
      contrato.
    </div>
  </div>
);

const thStyle = {
  padding: '10px 12px', textAlign: 'left', fontSize: '10.5px',
  letterSpacing: '2px', textTransform: 'uppercase', fontWeight: 900,
};
const tdStyle = {
  padding: '10px 12px', borderBottom: '1px solid #e2e8f0', fontSize: '12px',
  verticalAlign: 'top',
};

export default BajadaRentaDossier;
